import type { ReactNode } from 'react'
import { Button, Input, Radio, RadioGroup } from '@heroui/react'
import { nameIn, type StaffLanguage } from '../lib/staffText'
import { RELEASED_LANGUAGES } from '../lib/i18n/languages'
import type { Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { useTextLanguages } from '../lib/i18n/useCustomerLanguages'
import { missingLanguages } from '../lib/i18n/missing'
import { MissingMark } from '../ui/MissingMark'
import { SameSwitch } from '../ui/SameSwitch'
import { isSame, ownWords, withWords } from '../lib/i18n/same'
import { LANGUAGES } from './parts'
import { ChoiceSelect, Seg } from './settingsKit'
import '../styles/record.css'

/**
 * The pieces Outcomes, Marks, Customer details and Delivered services share: the
 * name in three languages, the code, the service scope, the reorder buttons and
 * the frame a preview sits in.
 *
 * Every one of these is at module level. A component defined inside another's
 * render is a new component on every render, so React remounts it and the text
 * box being typed into loses focus after one character — a bug this codebase has
 * shipped before.
 */

/** What an outcome's or a mark's code may be (internal/api/outcomes.go,
 *  outcomeCodeShape): capitals, digits, dash or underscore, at most 32. */
export const CODE_RE = /^[A-Z0-9_-]{1,32}$/

/** A code as it is typed: upper-cased and with spaces made underscores, so the
 *  field shows what will be stored rather than changing on save. */
export const codeTyped = (text: string) => text.toUpperCase().replace(/\s+/g, '_')

/** Whether any language has a name. The server asks for one, in any language. */
export const hasName = (name: Record<string, string>) => Object.values(name).some((v) => v.trim() !== '')

/** The names worth sending: a language left blank is left out rather than sent
 *  empty, because the server refuses a blank name in a language it was given
 *  (catalog.Name.Validate) and falls back to the other languages anyway. */
export function cleanNames(name: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(name)
      .map(([code, text]) => [code, text.trim()] as const)
      .filter(([, text]) => text !== ''),
  )
}

/** In the list's order: sortOrder, then code, which is how a desk sees them. */
export function inOrder<T extends { sortOrder: number; code: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
}

/**
 * The sortOrder each row needs after moving one of them by one place, as a list
 * of the rows whose number changes. Numbered in tens, so a later "Order" typed by
 * hand elsewhere still has room between two rows. Only what changed is sent.
 */
export function reorder<T extends { id: string; sortOrder: number }>(
  list: T[],
  id: string,
  by: number,
): Array<{ id: string; sortOrder: number }> {
  const i = list.findIndex((x) => x.id === id)
  const j = i + by
  if (i < 0 || j < 0 || j >= list.length) return []
  const next = [...list]
  ;[next[i], next[j]] = [next[j]!, next[i]!]
  return next
    .map((row, k) => ({ id: row.id, sortOrder: (k + 1) * 10, was: row.sortOrder }))
    .filter((row) => row.sortOrder !== row.was)
    .map(({ id: rowId, sortOrder }) => ({ id: rowId, sortOrder }))
}

/** A service's name in the reader's language, or what an empty scope means. */
export function useServiceName(services: Service[]) {
  const { lang, say } = useStaffText()
  return (id?: string) => {
    if (!id) return say('record.everyService')
    const service = services.find((s) => s.id === id)
    return service ? nameIn(service.name, lang, service.code) : say('record.serviceGone')
  }
}

/** The name in each language. Any one will do; the others fall back to it. */
export function NamesField({
  legend,
  value,
  onChange,
  customer,
}: {
  legend: string
  value: Record<string, string>
  onChange: (next: Record<string, string>) => void
  /** Customers read it: the languages it lacks are marked. */
  customer?: boolean
}) {
  const { say } = useStaffText()
  const TEXT_LANGUAGES = useTextLanguages()
  const started = Object.keys(value).length > 0
  const missing = customer
    ? missingLanguages(
        value,
        TEXT_LANGUAGES.map(([c]) => c),
      )
    : []
  const def = TEXT_LANGUAGES[0]![0]
  const same = customer && isSame(value)
  return (
    <fieldset className="field">
      <legend>
        {legend} <MissingMark codes={missing} />
      </legend>
      {same ? (
        <div className="record-names" data-single>
          <label className="record-names__row">
            <Input fullWidth value={ownWords(value, def)} aria-label={legend} onChange={(e) => onChange(withWords(value, def, e.target.value, def))} />
          </label>
        </div>
      ) : (
        <div className="record-names" data-single={TEXT_LANGUAGES.length === 1 || undefined}>
          {TEXT_LANGUAGES.map(([code, label]) => (
            <label key={code} className="record-names__row">
              {TEXT_LANGUAGES.length > 1 && <span className="record-names__lang">{label}</span>}
              <Input
                fullWidth
                value={value[code] ?? ''}
                lang={code}
                aria-label={TEXT_LANGUAGES.length > 1 ? undefined : legend}
                onChange={(e) => onChange({ ...value, [code]: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}
      {customer && (same || TEXT_LANGUAGES.length > 1) && <SameSwitch value={value} onChange={onChange} defaultLang={def} label={legend} />}
      {started && !hasName(value) && <small className="record-error">{say('record.nameNeeded')}</small>}
    </fieldset>
  )
}

/** The code a record is exported under. */
export function CodeField({
  value,
  onChange,
  taken,
  isNew,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  taken: boolean
  isNew: boolean
  placeholder: string
}) {
  const { say } = useStaffText()
  const bad = value !== '' && !CODE_RE.test(value)
  return (
    <label className="field">
      <span title={isNew ? undefined : say('record.codeKept')}>{say('record.code')}</span>
      <Input
        fullWidth
        className="record-code"
        value={value}
        placeholder={placeholder}
        aria-invalid={bad || taken || undefined}
        autoCapitalize="characters"
        spellCheck={false}
        onChange={(e) => onChange(codeTyped(e.target.value))}
      />
      {bad && <small className="record-error">{say('record.codeShape')}</small>}
      {taken && <small className="record-error">{say('record.codeTaken', { code: value })}</small>}
    </label>
  )
}

/** Every service, or one. */
export function ServiceScope({
  services,
  value,
  onChange,
}: {
  services: Service[]
  value: string
  onChange: (id: string) => void
}) {
  const { lang, say } = useStaffText()
  const one = value !== ''
  const offered = services.filter((s) => s.active || s.id === value)
  return (
    <fieldset className="field">
      <legend>{say('record.offeredFor')}</legend>
      <RadioGroup
        className="record-scope"
        name="record-scope"
        aria-label={say('record.offeredFor')}
        value={one ? 'one' : 'every'}
        onChange={(next) => onChange(next === 'one' ? (offered[0]?.id ?? '') : '')}
      >
        <Radio value="every">
          <Radio.Content>
            <Radio.Control>
              <Radio.Indicator />
            </Radio.Control>
            {say('record.everyService')}
          </Radio.Content>
        </Radio>
        <Radio value="one" isDisabled={offered.length === 0}>
          <Radio.Content>
            <Radio.Control>
              <Radio.Indicator />
            </Radio.Control>
            {say('record.oneService')}
          </Radio.Content>
        </Radio>
      </RadioGroup>
      {one && (
        <ChoiceSelect
          label={say('record.service')}
          value={value}
          onChange={onChange}
          options={offered.map((s) => ({ value: s.id, label: `${nameIn(s.name, lang, s.code)} · ${s.code}` }))}
        />
      )}
    </fieldset>
  )
}

/** Up and down beside a row: the list's order is the order the desk shows. */
export function MoveButtons({
  name,
  first,
  last,
  disabled,
  onMove,
}: {
  name: string
  first: boolean
  last: boolean
  disabled?: boolean
  onMove: (by: number) => void
}) {
  const { say } = useStaffText()
  return (
    <span className="record-move">
      <Button isIconOnly variant="ghost" size="sm" isDisabled={first || disabled} onPress={() => onMove(-1)} aria-label={say('record.moveUp', { name })}>
        <Icon name="up" size={16} />
      </Button>
      <Button isIconOnly variant="ghost" size="sm" isDisabled={last || disabled} onPress={() => onMove(1)} aria-label={say('record.moveDown', { name })}>
        <Icon name="down" size={16} />
      </Button>
    </span>
  )
}

/** Which language a preview speaks. The reader's own is where it starts; none while English alone is released. */
export function LangSwitch({
  lang,
  setLang,
}: {
  lang: StaffLanguage
  setLang: (next: StaffLanguage) => void
}) {
  const { say } = useStaffText()
  if (RELEASED_LANGUAGES.length < 2) return null
  return (
    <Seg
      label={say('record.previewLanguage')}
      value={lang}
      onChange={(next) => setLang(next as StaffLanguage)}
      options={LANGUAGES.map(([code, label]) => ({ value: code as StaffLanguage, label: code.toUpperCase(), title: label }))}
    />
  )
}

/** The frame around a preview of what the desk or the customer will see. */
export function Preview({ title, controls, children }: { title: string; controls?: ReactNode; children: ReactNode }) {
  return (
    <section className="record-preview" aria-label={title}>
      <header className="record-preview__head">
        <h3>{title}</h3>
        {controls}
      </header>
      <div className="record-preview__screen">{children}</div>
    </section>
  )
}

/** The line a list shows before its first answer, so a slow line never reads as empty. */
export function LoadingLine() {
  const { say } = useStaffText()
  return (
    <p className="empty" role="status">
      {say('common.loading')}
    </p>
  )
}
